import { z } from 'zod';

/** `prefix` + snake_case, e.g. prefixed('prod_') accepts 'prod_instant_noodle'. */
export const prefixed = <P extends string>(prefix: P) => {
  const re = new RegExp(`^${prefix}[a-z0-9_]+$`);
  return z.custom<`${P}${string}`>((v) => typeof v === 'string' && re.test(v), {
    message: `ID must look like ${prefix}<snake_case>`,
  });
};

export const zProductId = prefixed('prod_');
export const zFurnitureId = prefixed('fur_');
export const zDecorId = prefixed('dec_');
export const zFixtureId = prefixed('fix_');
export const zItemId = z.union([zProductId, zFurnitureId, zDecorId, zFixtureId]);
export const zWallFloorId = z.union([prefixed('wall_'), prefixed('floor_')]);
export const zSetId = prefixed('set_');
export const zUnlockId = prefixed('unl_');
export const zAchievementId = prefixed('ach_');
export const zNpcId = prefixed('npc_');
export const zMiniGameId = prefixed('mg_');
export const zPostcardId = prefixed('pc_');
export const zLetterId = prefixed('ltr_');
export const zStickerId = prefixed('stk_');
export const zPhotoFrameId = prefixed('frm_');
export const zAvatarFrameId = prefixed('afr_');
export const zTitleId = prefixed('ttl_');
export const zBadgeId = prefixed('bdg_');
export const zAvatarPartId = prefixed('av_');
export const zBoxDefId = prefixed('box_');
export const zExpansionId = prefixed('exp_');
export const zFlagId = prefixed('flag_');
export const zChapterId = z.custom<`ch${number}`>((v) => typeof v === 'string' && /^ch\d+$/.test(v), {
  message: 'Chapter ID must look like ch<number>',
});
export const zTaskId = z.custom<`T${string}` | `O${string}` | `rep_${string}`>(
  (v) => typeof v === 'string' && /^(T\d{2}|O\d{2}|rep_[a-z0-9_]+)$/.test(v),
  { message: 'Task ID must be T01, O01 or rep_<name>' },
);
/** Uppercase segments are allowed because story keys use task IDs (tutorial.T09.1). */
export const zI18nKey = z.string().regex(/^[a-z][A-Za-z0-9_]*(\.[A-Za-z0-9_]+)+$/, 'Invalid i18n key');
export const zDayKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const zRoomId = z.enum(['home', 'minimart', 'studio']);
export const zSceneId = z.enum(['home', 'minimart', 'studio', 'townmap']);
export const zZoneId = z.enum([
  'window',
  'kitchen',
  'corner',
  'center',
  'aisle',
  'door',
  'sleep_nook',
  'stage',
  'pose',
]);
export const zRotation = z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]);
export const zWallSide = z.enum(['left', 'right']);
export const zItemKind = z.enum(['product', 'furniture', 'decor', 'fixture']);
export const zItemLayer = z.enum(['floor', 'object', 'surface', 'wall']);
export const zThemeTag = z.enum(['cute', 'sweet', 'office', 'healthy', 'kids', 'garden', 'pet', 'cozy']);
export const zProductCategory = z.enum([
  'food',
  'drink',
  'snack',
  'stationery',
  'pet',
  'fruit',
  'home',
  'garden',
]);
export const zMaterial = z.enum(['paper', 'wood', 'glass', 'box', 'fabric', 'ceramic', 'metal', 'plant']);
export const zShopTab = z.enum(['furniture', 'decor', 'stock', 'wallpaper', 'plants', 'themes']);
export const zSurfaceTag = z.enum([
  'nightstand',
  'desk',
  'sofa',
  'table',
  'bookshelf',
  'kitchen_counter',
  'plant_stand',
  'counter',
  'shelf',
  'fridge',
  'basket',
]);
export const zFeatureKey = z.enum([
  'catalog',
  'shop',
  'mailbox',
  'townmap',
  'npcOrders',
  'photoMode',
  'albumPhotos',
  'studio',
]);
